import axiosApi from "../api/axiosInstance"; // axios instance with withCredentials: true
import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

const AuthContext = createContext(null);

/**
 * AuthProvider
 * - On mount: calls GET /api/v2/auth/me to populate user (works with httpOnly cookie)
 * - Exposes: authData, setAuthData, authLoading, signOut, refreshAuth
 */
export function AuthProvider({ children }) {
  const [authData, setAuthData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // true while checking session

  // fetch function extracted so we can call it from components after login
  const fetchMe = async () => {
    try {
      const res = await axiosApi.get("/api/v2/auth/me"); // api has withCredentials:true
      // response shape: { success, message, claims, tokenRecord }
      // console.log("Auth Me Response: ");
      // console.log(res);
      // console.log("-------------------");
      const tokenRecord = res?.data?.tokenRecord;
      if (tokenRecord) {
        setAuthData((prevAuthData) => {
          if (JSON.stringify(prevAuthData) === JSON.stringify(tokenRecord)) {
            return prevAuthData; // Avoid redundant updates
          }
          return {
            Id: tokenRecord.Id,
            CustomerId: tokenRecord.CustomerId,
            Provider: tokenRecord.Provider,
            ProviderCustomerCode: tokenRecord.ProviderCustomerCode,
            Token: tokenRecord.Token,
            ExpiresAt: tokenRecord.ExpiresAt,
            IsActive: tokenRecord.IsActive,
            Meta: tokenRecord.Meta,
            CreatedAt: tokenRecord.CreatedAt,
          };
        });
        return { success: true, data: tokenRecord };
      } else {
        setAuthData(null);
        return { success: false };
      }
    } catch (err) {
      setAuthData(null);
      return { success: false, error: err };
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mounted) return;
      await fetchMe();
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // small helper exposed so components can refresh auth after login
  const refreshAuth = async () => {
    setAuthLoading(true);
    const result = await fetchMe();
    setAuthLoading(false);
    if (!result.success) {
      toast.warn("Unable to fetch auth Data after login.");
    }
    return result;
  };

  const signOut = async () => {
    try {
      await axiosApi.post(
        "/api/v2/customer/logout",
        {},
        { skipUnauthorizedEvent: true }
      );
    } catch (e) {
      // ignore errors
    } finally {
      setAuthData(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{ authData, setAuthData, authLoading, signOut, refreshAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
